class ProductGrid extends HTMLElement {
  constructor() {
    super();
    // Cache these DOM attributes as class properties for better performance
    this.columns_desktop = null;
    this.columns_mobile = null;
    this.currentViewport = 'mobile';
    this.mediaQueryList = null;
    this.defaultLayout = 'normal-columns';
    this._resizeTimer = null;
  }

  connectedCallback() {
    try {
      this.updateColumns();
      // Initialize viewport detection
      this.initViewportDetection();

      // Bind events and initialize grid layout
      this.bindEvents();
      this.initializeLayoutFromAttribute();
      this.initializeGridLayout();
    } catch (error) {
      console.error('Error during ProductGrid initialization:', error);
      // Apply default layout as fallback
      this.updateGridLayout({detail: {newLayout: this.defaultLayout}});
    }
  }
  initializeLayoutFromAttribute() {
    const validLayouts = ['list', 'grid', 'dynamic'];
    if (!validLayouts.includes(this.dataset.layout)) {
      console.warn(`Invalid data-layout: ${this.dataset.layout}`);
      return;
    }
    const layoutMap = {
      list: 'single-column',
      grid: 'normal-columns',
      dynamic: null, // Let LayoutManager decide
    };

    // Force layout only if explicitly set (not dynamic)
    const mappedLayout = layoutMap[this.dataset.layout];
    if (mappedLayout) {
      localStorage.setItem('grid-layout', mappedLayout);
    }
  }
  updateColumns() {
    // Cache column values on initialization
    const originalDesktopCols =
      Number(this.getAttribute('data-desktop-columns')) || 3;
    const originalMobileCols =
      Number(this.getAttribute('data-mobile-columns')) || 2;

    // Check if sidebar is active
    const isSidebarActive = localStorage.getItem('sidebarActive') === 'true';

    // If active, reduce desktop column count by 1
    this.columns_desktop = isSidebarActive
      ? Math.max(originalDesktopCols - 1, 1) // ensure it doesn't go below 1
      : originalDesktopCols;

    this.columns_mobile = originalMobileCols;
  }

  disconnectedCallback() {
    // Clean up event listeners when element is removed
    document.removeEventListener(
      'layout:update',
      this._updateGridLayoutHandler,
    );
    window.removeEventListener('resize', this._handleResizeDebounced);

    // Clean up media query listener if it exists
    if (this.mediaQueryList) {
      this.mediaQueryList.removeEventListener(
        'change',
        this._handleMediaQueryChange,
      );
    }

    // Clear any pending debounced resize handler
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
    }
  }

  initViewportDetection() {
    // Use matchMedia to detect viewport width
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    this.currentViewport = mediaQuery.matches ? 'desktop' : 'mobile';
    this.mediaQueryList = mediaQuery;

    // Create handler for media query changes
    this._handleMediaQueryChange = (event) => {
      this.currentViewport = event.matches ? 'desktop' : 'mobile';
      // Re-apply current layout with new viewport size
      this.updateGridLayout({
        detail: {newLayout: LayoutManager.getCurrentLayout()},
      });
    };

    // Listen for viewport width changes
    this.mediaQueryList.addEventListener(
      'change',
      this._handleMediaQueryChange,
    );

    // Create a debounced resize handler as a backup
    this._handleResize = () => {
      const wasDesktop = this.currentViewport === 'desktop';
      const isNowDesktop = window.innerWidth >= 768;

      if (wasDesktop !== isNowDesktop) {
        this.currentViewport = isNowDesktop ? 'desktop' : 'mobile';
        this.updateGridLayout({
          detail: {newLayout: LayoutManager.getCurrentLayout()},
        });
      }
    };

    // Debounce the resize handler
    this._handleResizeDebounced = () => {
      if (this._resizeTimer) {
        clearTimeout(this._resizeTimer);
      }
      this._resizeTimer = setTimeout(this._handleResize, 250);
    };

    // Attach the backup resize handler
    window.addEventListener('resize', this._handleResizeDebounced);
  }

  bindEvents() {
    // Store the bound handler for potential cleanup in disconnectedCallback
    this._updateGridLayoutHandler = this.updateGridLayout.bind(this);
    document.addEventListener('layout:update', this._updateGridLayoutHandler);
  }

  initializeGridLayout() {
    try {
      let layout;
      if (this.dataset.layout == 'dynamic') {
        // Check the URL for the 'grid-layout' parameter first
        const urlParams = new URL(window.location.href).searchParams;
        if (urlParams.get('grid-layout')) {
          layout = urlParams.get('grid-layout');
        } else {
          layout = localStorage.getItem('grid-layout');
        }
      } else {
        layout = localStorage.getItem('grid-layout');
      }

      // Validate that the layout is one of the allowed values
      const validLayouts = ['normal-columns', 'single-column'];
      if (layout && !validLayouts.includes(layout)) {
        console.warn(
          `Invalid layout '${layout}' detected, falling back to default`,
        );
        layout = this.defaultLayout;
      }
      // If layout is found, update the URL and apply the layout
      if (layout) {
        this.updateURL(layout);
        this.updateGridLayout({detail: {newLayout: layout}});
      } else {
        // If no layout is found, apply the default layout
        this.updateGridLayout({detail: {newLayout: this.defaultLayout}});
      }
    } catch (error) {
      console.error('Error initializing grid layout:', error);
      this.updateGridLayout({detail: {newLayout: this.defaultLayout}});
    }
  }

  updateURL(newLayout) {
    // Get the current URL and update the grid-layout search parameter
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('grid-layout', newLayout);

      // Update the URL using pushState without reloading the page
      window.history.pushState({}, '', url.toString());
    } catch (error) {
      console.error('Failed to update URL:', error);
      // No need for fallback that reloads the page as it disrupts user experience
    }
  }

  updateGridLayout(event) {
    try {
      if (!event || !event.detail || !event.detail.newLayout) {
        console.error('Invalid event passed to updateGridLayout');
        return;
      }
      this.updateColumns();
      const currentLayout = event.detail.newLayout;

      // Common classes that all layouts share
      const baseClasses = 'leo-w-full leo-grid leo-product-grid';

      // Build the class list based on the layout and current viewport
      let classList = [baseClasses];
      if (currentLayout === 'all-columns') {
        // Apply different column counts based on current viewport
        const columnsClass =
          this.currentViewport === 'desktop'
            ? `lg:leo-grid-cols-${this.columns_desktop}`
            : `leo-grid-cols-${this.columns_mobile}`;

        classList.push(columnsClass);
        classList.push('leo-gap-x-2');
        classList.push('all-columns');
      } else if (currentLayout === 'single-column') {
        classList.push('leo-grid-cols-2');
        classList.push('max-sm:leo-grid-cols-1');
        classList.push('leo-gap-x-4');
        classList.push('single-column');
      } else {
        // Default to normal-columns layout
        const columnsClass =
          this.currentViewport === 'desktop'
            ? `lg:leo-grid-cols-${this.columns_desktop}`
            : `leo-grid-cols-${this.columns_mobile}`;

        classList.push(columnsClass);
        classList.push('leo-gap-x-4');
        classList.push(`sm:leo-grid-cols-${this.columns_desktop - 1}`);
        classList.push('normal-columns');
      }

      // Apply the new class list
      this.className = classList.join(' ');

      // Add data attribute for current viewport for styling purposes
      this.setAttribute('data-viewport', this.currentViewport);

      // Dispatch a custom event after layout is updated (for potential third-party integrations)
      this.dispatchEvent(
        new CustomEvent('product-grid:layout-updated', {
          detail: {layout: currentLayout, viewport: this.currentViewport},
          bubbles: true,
        }),
      );
    } catch (error) {
      console.error('Error updating grid layout:', error);
      // Apply default classes as fallback
      this.className = 'leo-w-full leo-grid leo-product-grid';
    }
  }
}
customElements.define('product-grid', ProductGrid);

// Utility functions for layout handling
class LayoutManager {
  // Valid layout options for validation
  static validLayouts = ['normal-columns', 'all-columns', 'single-column'];

  static getCurrentLayout() {
    try {
      // Try to get layout from URL parameters first
      const urlParams = new URL(window.location.href).searchParams;
      const urlLayout = urlParams.get('grid-layout');

      if (urlLayout && this.validLayouts.includes(urlLayout)) {
        return urlLayout;
      }

      // Then try localStorage
      const storedLayout = localStorage.getItem('grid-layout');
      if (storedLayout && this.validLayouts.includes(storedLayout)) {
        return storedLayout;
      }

      // Default fallback
      return 'normal-columns';
    } catch (error) {
      console.error('Error getting current layout:', error);
      return 'normal-columns'; // Safe default
    }
  }

  static setCurrentLayout(newLayout) {
    // Validate the layout
    if (!this.validLayouts.includes(newLayout)) {
      console.error(`Invalid layout: ${newLayout}. Using default instead.`);
      newLayout = 'normal-columns';
    }

    const oldLayout = this.getCurrentLayout();

    // Skip if no change
    if (oldLayout === newLayout) {
      return;
    }

    // Update localStorage
    try {
      localStorage.setItem('grid-layout', newLayout);
    } catch (error) {
      console.error('Failed to save layout to localStorage:', error);
    }

    // Update the URL without reloading the page
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('grid-layout', newLayout);
      window.history.pushState({}, '', url.toString());
    } catch (error) {
      console.error('Failed to update URL:', error);
    }

    // Track layout change for analytics if dataLayer is available
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'productGridLayoutChange',
        oldLayout: oldLayout,
        newLayout: newLayout,
      });
    }

    return {oldLayout, newLayout};
  }

  static dispatchLayoutUpdate(newLayout) {
    try {
      const oldLayout = this.getCurrentLayout();

      const event = new CustomEvent('layout:update', {
        detail: {newLayout, oldLayout},
        bubbles: true,
        cancelable: true,
        composed: false,
      });

      document.dispatchEvent(event);
    } catch (error) {
      console.error('Error dispatching layout update:', error);
    }
  }

  static applyActiveClass(element, isActive) {
    if (!element) return;

    try {
      if (isActive) {
        element.classList.add('active');
        element.setAttribute('aria-pressed', 'true');
      } else {
        element.classList.remove('active');
        element.setAttribute('aria-pressed', 'false');
      }
    } catch (error) {
      console.error('Error applying active class:', error);
    }
  }
}

// Base class for shared behavior
class LayoutToggleBase extends HTMLElement {
  constructor() {
    super();
    // Store bound event handlers for potential cleanup
    this._toggleHandler = this.toggleLayout.bind(this);
    this._keydownHandler = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    // Add accessibility attributes
    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');
    this.setAttribute('aria-pressed', 'false');

    if (!this.hasAttribute('aria-label')) {
      // Generate default aria-label based on class name if not provided
      const layoutType = this.constructor.name
        .replace('Show', '')
        .replace(/([A-Z])/g, ' $1')
        .trim();
      this.setAttribute('aria-label', `Switch to ${layoutType} view`);
    }

    this.bindEvents();
    this.updateActiveState();
  }

  bindEvents() {
    this.addEventListener('click', this._toggleHandler);
    this.addEventListener('keydown', this._keydownHandler);
  }

  disconnectedCallback() {
    // Clean up event listeners when element is removed
    this.removeEventListener('click', this._toggleHandler);
    this.removeEventListener('keydown', this._keydownHandler);
  }

  handleKeydown(event) {
    // Support keyboard navigation (Enter or Space)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleLayout();
    }
  }

  toggleLayout() {
    const currentLayout = LayoutManager.getCurrentLayout();
    const newLayout = this.getNewLayout(currentLayout);

    if (currentLayout === newLayout) {
      return; // Avoid redundant updates
    }

    LayoutManager.setCurrentLayout(newLayout);
    LayoutManager.dispatchLayoutUpdate(newLayout);
    this.updateActiveState();
  }

  updateActiveState() {
    const currentLayout = LayoutManager.getCurrentLayout();
    const isActive = this.isActiveLayout(currentLayout);

    // Apply active class to the current toggle button
    LayoutManager.applyActiveClass(this, isActive);

    // Update ARIA state
    this.setAttribute('aria-pressed', isActive ? 'true' : 'false');

    // Toggle active class on other toggle buttons
    const otherToggleSelectors = this.getAttribute(
      'data-other-toggle-selector',
    );
    if (otherToggleSelectors) {
      try {
        const otherToggles = document.querySelectorAll(otherToggleSelectors);

        otherToggles.forEach((toggle) => {
          const isOtherActive =
            toggle.getAttribute('data-layout') === currentLayout;
          LayoutManager.applyActiveClass(toggle, isOtherActive);

          // Also update ARIA state on other toggles if they're LayoutToggleBase elements
          if (toggle instanceof LayoutToggleBase) {
            toggle.setAttribute(
              'aria-pressed',
              isOtherActive ? 'true' : 'false',
            );
          }
        });
      } catch (error) {
        console.error('Error updating other toggle states:', error);
      }
    }
  }

  // Abstract methods to be overridden by subclasses
  getNewLayout(currentLayout) {
    throw new Error('getNewLayout() must be implemented by subclass');
  }

  isActiveLayout(currentLayout) {
    throw new Error('isActiveLayout() must be implemented by subclass');
  }
}

// Specific implementation for "Show All Columns"
class ShowAllColumns extends LayoutToggleBase {
  connectedCallback() {
    // Set a specific, descriptive aria-label before calling super
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute(
        'aria-label',
        'Switch to compact grid view with maximum columns',
      );
    }
    super.connectedCallback();
  }

  getNewLayout(currentLayout) {
    return 'all-columns';
  }

  isActiveLayout(currentLayout) {
    return currentLayout === 'all-columns';
  }
}
customElements.define('show-all-columns', ShowAllColumns);

// Specific implementation for "Show Normal Columns"
class ShowNormalColumns extends LayoutToggleBase {
  connectedCallback() {
    // Set a specific, descriptive aria-label before calling super
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Switch to standard grid view');
    }
    super.connectedCallback();
  }

  getNewLayout(currentLayout) {
    return 'normal-columns';
  }

  isActiveLayout(currentLayout) {
    return currentLayout === 'normal-columns';
  }
}
customElements.define('show-normal-columns', ShowNormalColumns);

// Specific implementation for "Show List View"
class ShowListView extends LayoutToggleBase {
  connectedCallback() {
    // Set a specific, descriptive aria-label before calling super
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Switch to single column list view');
    }
    super.connectedCallback();
  }

  getNewLayout(currentLayout) {
    return 'single-column'; // The layout identifier for list view
  }

  isActiveLayout(currentLayout) {
    return currentLayout === 'single-column';
  }
}
customElements.define('show-list-view', ShowListView);
